import { cn } from '../../shared/utils/cn'
import { MultiExportProgress, DeviceExportStatus } from '../../utils/csvExportTypes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faSpinner, faExclamationCircle, faClock } from '@fortawesome/free-solid-svg-icons'

interface ExportMultiProgressProps {
  progress: MultiExportProgress
}

function getStatusIcon(status: DeviceExportStatus['status']) {
  switch (status) {
    case 'pending':
      return <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5 text-gray-400" />
    case 'fetching':
    case 'generating':
      return <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 text-blue-500 animate-spin" />
    case 'complete':
      return <FontAwesomeIcon icon={faCheck} className="h-3.5 w-3.5 text-green-500" />
    case 'error':
      return <FontAwesomeIcon icon={faExclamationCircle} className="h-3.5 w-3.5 text-red-500" />
  }
}

function getStatusLabel(status: DeviceExportStatus['status']) {
  switch (status) {
    case 'pending':
      return 'Waiting'
    case 'fetching':
      return 'Fetching data...'
    case 'generating':
      return 'Generating CSV...'
    case 'complete':
      return 'Done'
    case 'error':
      return 'Failed'
  }
}

export function ExportMultiProgress({ progress }: ExportMultiProgressProps) {
  const { totalDevices, completedDevices, deviceStatuses } = progress
  const percentage = totalDevices > 0
    ? Math.round((completedDevices / totalDevices) * 100)
    : 0

  // Sort devices: in-progress first, then pending, then complete/error
  const sortedStatuses = Object.values(deviceStatuses).sort((a, b) => {
    const order = { fetching: 0, generating: 0, pending: 1, complete: 2, error: 2 }
    return order[a.status] - order[b.status]
  })

  return (
    <div className="space-y-3">
      {/* Overall Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 font-medium">
            Exporting {completedDevices} of {totalDevices} devices
          </span>
          <span className="text-gray-500">{percentage}%</span>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-300 ease-out',
              completedDevices === totalDevices ? 'bg-green-500' : 'bg-blue-500'
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Per-Device Status List */}
      <div className="max-h-[200px] overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
        {sortedStatuses.map((device) => (
          <div
            key={device.sigfoxId}
            className={cn(
              'flex items-center justify-between px-3 py-2 text-sm',
              device.status === 'fetching' || device.status === 'generating'
                ? 'bg-blue-50/50'
                : device.status === 'error'
                  ? 'bg-red-50/50'
                  : device.status === 'complete'
                    ? 'bg-green-50/30'
                    : 'bg-white'
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              {getStatusIcon(device.status)}
              <span className={cn(
                'truncate',
                device.status === 'complete' ? 'text-gray-500' : 'text-gray-700'
              )}>
                {device.deviceName}
              </span>
            </div>
            <span className={cn(
              'text-xs shrink-0 ml-2',
              device.status === 'error' ? 'text-red-600' : 'text-gray-500'
            )}>
              {device.error || getStatusLabel(device.status)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
